/* eslint-disable react-refresh/only-export-components */
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, CheckCircle, X } from 'lucide-react';
import { useHybridCart } from '@/contexts/HybridCartContext';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

interface CartNotificationProps {
  show: boolean;
  productName?: string;
  onClose: () => void;
}

export const CartNotification = ({ show, productName, onClose }: CartNotificationProps) => {
  const { totalItems, subtotal } = useHybridCart();
  const navigate = useNavigate();
  const [autoCloseTimer, setAutoCloseTimer] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (show) {
      // Auto close after 4 seconds
      const timer = setTimeout(() => {
        onClose();
      }, 4000);
      setAutoCloseTimer(timer);

      return () => {
        if (timer) clearTimeout(timer);
      };
    }
  }, [show, onClose]);

  const handleViewCart = () => {
    onClose();
    navigate('/cart');
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, x: 400, scale: 0.8 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, x: 400, scale: 0.8 }}
          transition={{ 
            type: "spring", 
            stiffness: 500, 
            damping: 30,
            duration: 0.3 
          }}
          className="fixed top-4 right-4 z-[100] max-w-sm w-full"
        >
          <div className="bg-white border border-green-200 rounded-2xl shadow-2xl p-4 backdrop-blur-md bg-white/95">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <div className="bg-green-100 rounded-full p-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                </div>
                <h3 className="font-semibold text-brown-dark text-sm">
                  ¡Agregado al carrito!
                </h3>
              </div>
              <button
                onClick={onClose}
                className="text-brown/60 hover:text-brown-dark transition-colors p-1"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Product Info */}
            {productName && (
              <p className="text-brown text-sm mb-3 line-clamp-2">
                <span className="font-medium">{productName}</span> se añadió a tu carrito
              </p>
            )}

            {/* Cart Summary */}
            <div className="bg-primary/10 rounded-lg p-3 mb-3">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-2">
                  <ShoppingBag className="h-4 w-4 text-brown" />
                  <span className="text-brown font-medium">
                    {totalItems} producto{totalItems !== 1 ? 's' : ''}
                  </span>
                </div>
                <span className="font-bold text-brown-dark">
                  USD {subtotal.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={onClose}
                className="flex-1 text-xs"
              >
                Continuar comprando
              </Button>
              <Button
                size="sm"
                onClick={handleViewCart}
                className="flex-1 bg-primary hover:bg-primary-dark text-brown-dark text-xs"
              >
                Ver carrito
              </Button>
            </div>

            {/* Progress Bar */}
            <motion.div
              initial={{ width: "100%" }}
              animate={{ width: "0%" }}
              transition={{ duration: 4, ease: "linear" }}
              className="absolute bottom-0 left-0 h-1 bg-primary rounded-b-2xl"
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Hook para usar la notificación del carrito
export const useCartNotification = () => {
  const [notification, setNotification] = useState<{
    show: boolean;
    productName?: string;
  }>({ show: false });

  const showNotification = (productName?: string) => {
    setNotification({ show: true, productName });
  };

  const hideNotification = () => {
    setNotification({ show: false });
  };

  return {
    notification,
    showNotification,
    hideNotification,
  };
};